// Step 1: Basic Info Component - Refactored with Extracted Components
import React, { useMemo } from 'react';
import { Stack, Text, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconMapPin } from '@tabler/icons-react';
import { useDeviceLayout } from '../../../../../hooks';
import { AppTextarea } from '@/components/common/AppTextarea';
import {
  MAX_TASK_DESCRIPTION_LENGTH,
  TEXTAREA_MAX_ROWS,
  TEXTAREA_MIN_ROWS,
} from '@/constants';
import { FormSection } from '../../../../common/form';
import { getStringError } from '../../utils/formErrorHelpers';
import { Step1BasicInfoProps } from './Step1BasicInfo.types';
import { BasicInfoFields } from './components';
import { RoadDisplayField } from '@/components/common/RoadDisplayField';
import { ColoredSegmentedControl } from '@/components/common/ColoredSegmentedControl';
import { translatePriority } from '@/utils/enumTranslation';
import { Priority, PRIORITY_COLORS } from '@/types';
import styles from './Step1BasicInfo.module.css';

const PRIORITY_ORDER: readonly Priority[] = [
  Priority.Low,
  Priority.Medium,
  Priority.High,
  Priority.Urgent,
];

export const Step1BasicInfo = React.memo<Step1BasicInfoProps>(
  ({
    form,
    gpsLocationSuggestion,
    isLoading = false,
    handleManualRoadInput,
    handleRequestGPSDetection,
    roadSource,
    isWaitingForInitialGPS = false,
    members,
    isMembersLoading,
  }) => {
    const { t } = useTranslation();
    const { isMobile } = useDeviceLayout();

    const priorityOptions = useMemo(
      () =>
        PRIORITY_ORDER.map((priorityValue) => ({
          value: priorityValue,
          label: translatePriority(priorityValue, t),
        })),
      [t]
    );

    // Road-related utilities now handled by RoadDisplayField component

    return (
      <Stack gap="lg">
        {/* Basic Information Section */}
        <FormSection
          title={t('taskForm.basicInfo')}
          description={t('taskForm.essentialDetails')}
        >
          {/* Task Title */}
          <TextInput
            label={t('common.title')}
            placeholder={t('taskForm.titlePlaceholder')}
            required
            leftSection={<IconMapPin size={16} />}
            disabled={isLoading}
            autoComplete="off"
            autoFocus={!isMobile}
            mb="lg"
            value={form.values.title}
            onChange={(e) => form.setFieldValue('title', e.target.value)}
            error={form.errors.title}
          />

          {/* Task Description */}
          <AppTextarea
            label={t('wizard.step2.taskDescription')}
            placeholder={t('wizard.step2.taskDescriptionPlaceholder')}
            minRows={TEXTAREA_MIN_ROWS}
            maxRows={TEXTAREA_MAX_ROWS}
            autosize
            maxLength={MAX_TASK_DESCRIPTION_LENGTH}
            disabled={isLoading}
            mb="lg"
            {...form.getInputProps('description')}
          />

          {/* Priority Selection */}
          <div className={styles.prioritySection}>
            <Text size="sm" fw={500} mb="xs">
              {t('common.priority')}{' '}
              <Text span c="red.6">
                *
              </Text>
            </Text>
            <ColoredSegmentedControl
              fullWidth
              data={priorityOptions}
              disabled={isLoading}
              value={form.values.priority}
              onChange={(value) =>
                form.setFieldValue('priority', value as Priority)
              }
              color={PRIORITY_COLORS[form.values.priority]}
            />
          </div>

          {/* Basic Info Fields (Assignee and Due Date) */}
          <div className={styles.basicInfoFieldsContainer}>
            <BasicInfoFields
              form={form}
              isLoading={isLoading}
              members={members}
              isMembersLoading={isMembersLoading}
            />
          </div>

          {/* Road Selection */}
          <div className={styles.formField}>
            <RoadDisplayField
              value={form.values.location || ''}
              onChange={(value) => form.setFieldValue('location', value)}
              isDetectingGPS={gpsLocationSuggestion.isDetecting}
              isWaitingForInitialGPS={isWaitingForInitialGPS}
              gpsError={gpsLocationSuggestion.error}
              roadSource={roadSource}
              disabled={isLoading}
              error={getStringError(form.errors.location)}
              onRequestGPSDetection={handleRequestGPSDetection}
              onManualRoadInput={handleManualRoadInput}
            />
          </div>
        </FormSection>
      </Stack>
    );
  }
);
